import { Suspense, lazy, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Providers } from './providers';
import { AppShell } from './AppShell';
import { ErrorBoundary } from '@/lib/ui/ErrorBoundary';
import { LoadingState } from '@/lib/ui/states';
import { useAuth } from '@/lib/auth/useAuth';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from '@/features/auth/ForgotResetPages';
import { SiteMapPage } from '@/features/pageEditor/SiteMapPage';
import { firstInbox } from '@/config/nav';
import { MediaPickerHost } from '@/features/media/MediaPickerHost';
import { MediaWarningsToaster } from '@/features/brand/MediaWarningsToaster';
import { savePendingConsent } from '@/features/oauth/pendingConsent';

/*
 * Every route below the shell is code-split: nobody opens every screen in a session, and
 * none of them is needed to paint the shell. Each arrives on the click that asks for it,
 * behind the one Suspense boundary in `SuspendedRoutes`.
 *
 * Three pages stay eager on purpose: the sign-in and forgot/reset screens, which are the
 * first paint for a signed-out visitor, and the website home (the site map), where every
 * session lands. The overview (metrics and activity) is one click further and lazy.
 */
const AccountPage = lazy(() =>
  import('@/features/auth/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ScreenEditorPage = lazy(() =>
  import('@/features/pageEditor/ScreenEditorPage').then((m) => ({ default: m.ScreenEditorPage })),
);
const PageEditorPage = lazy(() =>
  import('@/features/pages/PageEditorPage').then((m) => ({ default: m.PageEditorPage })),
);
const FamiliesListPage = lazy(() =>
  import('@/features/families/FamiliesListPage').then((m) => ({ default: m.FamiliesListPage })),
);
const FamilyEditorPage = lazy(() =>
  import('@/features/families/FamilyEditorPage').then((m) => ({ default: m.FamilyEditorPage })),
);
const ContentListPage = lazy(() =>
  import('@/features/content/ContentListPage').then((m) => ({ default: m.ContentListPage })),
);
const ContentEditorPage = lazy(() =>
  import('@/features/content/ContentEditorPage').then((m) => ({ default: m.ContentEditorPage })),
);
const MediaLibraryPage = lazy(() =>
  import('@/features/media/MediaLibraryPage').then((m) => ({ default: m.MediaLibraryPage })),
);
const LeadsPage = lazy(() =>
  import('@/features/leads/LeadsPage').then((m) => ({ default: m.LeadsPage })),
);
const AuditPage = lazy(() =>
  import('@/features/audit/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const UsersPage = lazy(() =>
  import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const PageCopyPage = lazy(() =>
  import('@/features/pageCopy/PageCopyPage').then((m) => ({ default: m.PageCopyPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const ApiTokensPage = lazy(() =>
  import('@/features/apiTokens/ApiTokensPage').then((m) => ({ default: m.ApiTokensPage })),
);
const ConnectedAppsPage = lazy(() =>
  import('@/features/oauth/ConnectedAppsPage').then((m) => ({ default: m.ConnectedAppsPage })),
);
const AiConnectionsPage = lazy(() =>
  import('@/features/oauth/AiConnectionsPage').then((m) => ({ default: m.AiConnectionsPage })),
);
/* The OAuth consent and error screens render OUTSIDE the shell: an AI app sends the user
   here mid-flow, and the page must read as a self-contained decision, not as one more
   console screen with a sidebar of unrelated actions. */
const ConsentPage = lazy(() =>
  import('@/features/oauth/ConsentPage').then((m) => ({ default: m.ConsentPage })),
);
const OAuthErrorPage = lazy(() =>
  import('@/features/oauth/OAuthErrorPage').then((m) => ({ default: m.OAuthErrorPage })),
);

export function App() {
  return (
    /* The TRUE outermost net, above the providers themselves: a throw from the query
       client, the toast host or the auth provider has no other boundary above it and would
       unmount the root, leaving a white tab. */
    <ErrorBoundary>
      <Providers>
        {/* Catches errors in the shell/auth chrome, below the providers. */}
        <ErrorBoundary>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/forgot" element={<ForgotPasswordPage />} />
              <Route path="/reset" element={<ResetPasswordPage />} />
              <Route
                path="/oauth/consent"
                element={
                  <RequireAuth>
                    <StandaloneSuspense>
                      <ConsentPage />
                    </StandaloneSuspense>
                  </RequireAuth>
                }
              />
              <Route
                path="/oauth/error"
                element={
                  <StandaloneSuspense>
                    <OAuthErrorPage />
                  </StandaloneSuspense>
                }
              />
              <Route
                path="/*"
                element={
                  <RequireAuth>
                    <AppShell>
                      {/* Per-route boundary: a crash in one page is isolated to the content
                          area and clears automatically when the user navigates elsewhere. */}
                      <RouteErrorBoundary>
                        {/* The media/entity picker host is mounted HERE, not in `Providers`:
                            the picker renders a `MediaBrowser`, which reads router state, so
                            it must be inside `<BrowserRouter>`. Inside `RouteErrorBoundary`
                            too, so a crash in the picker costs only the content area. */}
                        <MediaPickerHost>
                          <MediaWarningsToaster />
                          <SuspendedRoutes>
                            <Route path="/" element={<HomeRoute />} />
                            <Route path="/overview" element={<DashboardPage />} />
                            <Route path="/account" element={<AccountPage />} />
                            <Route path="/account/connected-apps" element={<ConnectedAppsPage />} />
                            <Route path="/pages" element={<Navigate to="/" replace />} />
                            <Route path="/pages/screen/:key" element={<ScreenEditorPage />} />
                            <Route path="/pages/:id" element={<PageEditorPage />} />
                            <Route path="/page-copy/*" element={<PageCopyPage />} />
                            <Route path="/families" element={<FamiliesListPage />} />
                            <Route path="/families/:id" element={<FamilyEditorPage />} />
                            <Route path="/content/:collection" element={<ContentListPage />} />
                            <Route
                              path="/content/:collection/:id"
                              element={<ContentEditorPage />}
                            />
                            <Route path="/media" element={<MediaLibraryPage />} />
                            <Route path="/leads/:type" element={<LeadsPage />} />
                            <Route path="/leads/:type/:id" element={<LeadPathRedirect />} />
                            <Route path="/audit" element={<AuditPage />} />
                            <Route path="/users" element={<UsersPage />} />
                            <Route path="/tokens" element={<ApiTokensPage />} />
                            <Route path="/ai-connections" element={<AiConnectionsPage />} />
                            <Route path="/settings/*" element={<SettingsPage />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                          </SuspendedRoutes>
                        </MediaPickerHost>
                      </RouteErrorBoundary>
                    </AppShell>
                  </RequireAuth>
                }
              />
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </Providers>
    </ErrorBoundary>
  );
}

/**
 * `<Routes>` with the boundary the lazy routes need, in the content area, so a chunk that
 * is still arriving shows a skeleton where the page will be rather than collapsing the
 * shell. A chunk that fails to load (an old tab after a deploy) is caught by the route
 * boundary above, which offers "reload to update".
 */
function SuspendedRoutes({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Suspense fallback={<LoadingState label={t('common.loading')} />}>
      <Routes>{children}</Routes>
    </Suspense>
  );
}

/**
 * The Suspense boundary for a lazy page rendered outside the shell (OAuth consent/error):
 * there is no content area to put a skeleton in, so the fallback is a full-screen spinner
 * on the same canvas the page itself paints.
 */
function StandaloneSuspense({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-canvas-sand">
          <span
            aria-label={t('common.loading')}
            className="h-[28px] w-[28px] animate-spin rounded-pill border-2 border-accent-blue border-e-transparent"
          />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/** Route-scoped error boundary that resets whenever the path changes. */
function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

/** Auth guard: shows a splash while bootstrapping, redirects to login preserving deep-link. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { status, reconnecting, setPendingDeepLink } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    // A boot that is merely slow shows the spinner alone. One that is being RETRIED — the
    // server is rate-limiting or restarting — says so, because a spinner that looks hung
    // invites a reload, which spends another request against the same limit.
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-md bg-canvas-sand px-md text-center">
        <span
          aria-label={t('common.loading')}
          className="h-[28px] w-[28px] animate-spin rounded-pill border-2 border-accent-blue border-e-transparent"
        />
        {reconnecting && (
          <div role="status" aria-live="polite" className="flex flex-col gap-2xs">
            <p className="text-component-title text-ink-deep">{t('common.reconnecting')}</p>
            <p className="max-w-sm text-small text-ink-mid">{t('common.reconnectingBody')}</p>
          </div>
        )}
      </div>
    );
  }
  if (status === 'unauthenticated') {
    const deepLink = location.pathname + location.search;
    // Only an in-app path is replayed after login: never `//host` or its backslash form,
    // which some routers and browsers resolve to another origin.
    if (deepLink !== '/' && !/^\/[\\/]/.test(deepLink)) setPendingDeepLink(deepLink);
    // An OAuth consent request also survives a reload of the login page (sessionStorage);
    // `savePendingConsent` ignores every other path.
    savePendingConsent(deepLink);
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/**
 * Where a session lands: the website, page by page. Someone who cannot read the website's
 * content (a sales account) lands on the first inbox they can read instead, and anyone with
 * neither on the overview.
 */
function HomeRoute() {
  const { can } = useAuth();
  if (can('content:read')) return <SiteMapPage />;
  return <Navigate to={firstInbox(can) ?? '/overview'} replace />;
}

/**
 * Notification emails link to `/leads/<type>/<id>` (backend notify.go); the inbox opens a
 * record from `?id=`, so the path form is folded into it rather than dropped at `*`.
 */
function LeadPathRedirect() {
  const { type = '', id = '' } = useParams();
  return (
    <Navigate to={`/leads/${encodeURIComponent(type)}?id=${encodeURIComponent(id)}`} replace />
  );
}
